import React, { useMemo, useState } from "react";
import { CollapsibleSection } from "../components/ui/CollapsibleSection";
import { useLineItemTemplates } from "../hooks/useLineItemTemplates";
import { MitToReconMapping } from "@rebuild/shared";
import { MIT_TO_RECON_SOURCE_SCOPES } from "./mitToRecon.constants";
import { matchesScopeFilter, MitToReconScopeFilter, MitToReconScopeFilterOption } from "./MitToReconScopeFilter";

interface MitToReconMissingMappingsProps {
	mappings: MitToReconMapping[];
	onSelectLineItem: (lineItemId: string) => void;
}

export const MitToReconMissingMappings: React.FC<MitToReconMissingMappingsProps> = ({ mappings, onSelectLineItem }) => {
	const { dbLineItems, isLoading } = useLineItemTemplates();
	const [scopeFilter, setScopeFilter] = useState<MitToReconScopeFilterOption>("all");

	const missingLineItems = useMemo(() => {
		const mappedIds = new Set(mappings.map((m) => m.mitLineItem?.lineItemId).filter(Boolean));
		return dbLineItems
			.map((li) => ({
				lineItem: li,
				sourceScopes: MIT_TO_RECON_SOURCE_SCOPES.filter(({ scope }) => li.scopes?.includes(scope)),
			}))
			.filter(({ lineItem, sourceScopes }) => sourceScopes.length > 0 && !mappedIds.has(lineItem.id));
	}, [mappings, dbLineItems]);

	const filteredMissingLineItems = useMemo(
		() => missingLineItems.filter(({ lineItem }) => matchesScopeFilter(lineItem.scopes, scopeFilter)),
		[missingLineItems, scopeFilter],
	);

	if (isLoading) return null;

	return (
		<CollapsibleSection
			title="Missing Mappings"
			badge={missingLineItems.length > 0 ? `${missingLineItems.length} missing` : undefined}
		>
			{missingLineItems.length > 0 ? (
				<div>
					<MitToReconScopeFilter selectedFilter={scopeFilter} onChange={setScopeFilter} />
					<p className="text-sm font-medium text-gray-700 mb-2">Line Items Without a Mapping ({filteredMissingLineItems.length})</p>
					{filteredMissingLineItems.length > 0 ? (
						<div className="flex flex-wrap gap-2">
							{filteredMissingLineItems.map(({ lineItem, sourceScopes }) => (
								<button
									key={lineItem.id}
									onClick={() => onSelectLineItem(lineItem.id)}
									title="Add mapping"
									className="flex items-center gap-2 text-sm px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-medium hover:bg-blue-50 hover:border-blue-400 transition-colors"
								>
									{lineItem.originalLineItem}
									{sourceScopes.map(({ scope, label, tagClassName }) => (
										<span key={scope} className={`text-xs px-2 py-0.5 rounded-full font-normal ${tagClassName}`}>
											{label}
										</span>
									))}
								</button>
							))}
						</div>
					) : (
						<p className="text-sm text-gray-600">No missing mappings match this scope.</p>
					)}
				</div>
			) : (
				<p className="text-sm text-gray-600">All water mitigation and mold line items have a mapping.</p>
			)}
		</CollapsibleSection>
	);
};
